import { Button, Checkbox, CircularProgress, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import classNames from 'classnames';
import Dropdown, { createDropdownItem, DropdownItem } from 'components/Dropdown/Dropdown';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { escapeEmoji } from 'utils/format-text';

import config, { Country } from '../../config';
import { YAGA_EXTERNALS } from '../../config/fe-routes';
import { useLanguage } from '../../hooks/useLanguage';
import useUser from '../../hooks/useUser';
import CountryService, { ApiCountry } from '../../services/country.service';
import ShopService from '../../services/shop.service';
import UserService from '../../services/user.service';
import { setUser } from '../../store/actions/user.actions';
import { palette } from '../../themes/default';
import { GTM, GTMEvents } from '../../utils/analytics';
import { isNil } from '../../utils/is-nil';
import { useNotification } from '../../utils/snackbar';
import { hostnameEndsWithDomain } from '../../utils/url';
import Modal from '../Modal/Modal';

const userService = new UserService();
const countryService = new CountryService();
const shopService = new ShopService();

enum OnboardingStep {
  CONSENT = 'consent',
  USER_SETTINGS = 'user_settings',
}

export const Onboarding = () => {
  const { user, refreshUser } = useUser();
  const { t } = useTranslation();
  const { country } = useLanguage();
  const { showErrorNotification } = useNotification();

  const shopSlugTimeoutRef = useRef<NodeJS.Timeout>();

  const [isOnboardingVisible, setOnboardingVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(OnboardingStep.CONSENT);

  const [acceptOffers, setAcceptOffers] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [trackingInited, setTrackingInited] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<DropdownItem>();
  const [availableCountries, setAvailableCountries] = useState<ApiCountry[]>([]);
  const [firstName, setFirstName] = useState<string>(user?.firstName ?? '');
  const [lastName, setLastName] = useState<string>(user?.lastName ?? '');
  const [shopName, setShopName] = useState<string>(user?.shop?.name ?? '');
  const [shopSlug, setShopSlug] = useState<string>(user?.shop?.activeSlug ?? '');

  const [isShopSlugLoading, setShopSlugLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setAcceptOffers(user.consentMarketingCommunications ?? false);
      setFirstName(user?.firstName ?? '');
      setLastName(user?.lastName ?? '');
      setShopName(user?.shop?.name ?? '');
      setShopSlug(user?.shop?.activeSlug ?? '');
    }
  }, [user]);

  const brandsImg = {
    [Country.EE]: <img src="/images/consent/onboarding-brands-ee.png" alt="" className="onboarding-brands-img" draggable={false} />,
    [Country.ZA]: <img src="/images/consent/onboarding-brands-za.png" alt="" className="onboarding-brands-img" draggable={false} />,
  }[country];

  const isBrandsImagVisible = [Country.EE, Country.ZA].includes(country);

  const fetchCountries = async () => {
    try {
      const response = await countryService.getAvailableCountries();

      setAvailableCountries(response?.countries || []);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const onChangeShopName = async (newShopName: string) => {
    setShopName(newShopName);

    clearTimeout(shopSlugTimeoutRef.current);

    // If name is unchanged, keep slug unchanged.
    if (newShopName === user?.shop?.name) {
      setShopSlug(user?.shop?.activeSlug);
      setShopSlugLoading(false);

      return;
    }

    setShopSlugLoading(true);
    shopSlugTimeoutRef.current = setTimeout(async () => {
      const response = await shopService.getNewShopSlug(newShopName);

      setShopSlug(response.shopSlug ?? '');
      setShopSlugLoading(false);
    }, 250);
  };

  const handleConsentCTA = () => {
    if (!acceptTerms) {
      return;
    }

    if (!user?.onboardingWebCompleted) {
      setCurrentStep(OnboardingStep.USER_SETTINGS);
    } else {
      handleUserSettingsCTA();
    }
  };

  const handleUserSettingsCTA = async () => {
    // First update user shop related settings, then we can keep onboarding open after refresh still if something goes wrong.
    try {
      // Update shop data.
      await shopService.updateShop(
        user.shop.id,
        {
          name: shopName,
        },
        { silentFail: true },
      );

      // Update slug.
      if (user?.shop?.activeSlug !== shopSlug) {
        await shopService.updateShopSlug(user.shop.id, shopSlug, { silentFail: true });
      }
    } catch (error) {
      showErrorNotification({ error });

      return;
    }

    try {
      await Promise.all(
        [
          userService.acceptTerms(),

          // Update marketing communications consent.
          userService.updateUserConsent({
            consentMarketingCommunications: acceptOffers,
            marketing_email_notifications_source: 'onboarding',
          }),

          userService.updateUser({
            onboardingWebCompleted: true,
            country: selectedCountry?.value,
            firstName,
            lastName,
          }),
        ].filter((p) => !isNil(p)),
      );
    } catch (error) {
      if (error?.data?.data?.errorCode === 'NAME_VALIDATION_REJECTED') {
        GTM.logEvent(GTMEvents.NAME_VALIDATION_REJECTED);
      }

      showErrorNotification({ error });

      return;
    }

    // Re-fetch user so requiresTermsAcceptance is computed fresh from the server.
    const freshUser = await refreshUser();

    setUser({
      ...freshUser,
      shop: {
        ...freshUser.shop,
        name: shopName,
        activeSlug: shopSlug,
      },
    });

    // Track end of onboarding.
    GTM.logEvent(GTMEvents.ONBOARDING_COMPLETE, { userId: user?.id, reConsent: user?.requiresTermsAcceptance });

    setOnboardingVisible(false);
  };

  const getModalContent = () => {
    switch (currentStep) {
      case OnboardingStep.CONSENT:
        return (
          <div className="onboarding">
            {/* Onboarding. */}
            {!user?.requiresTermsAcceptance && (
              <>
                <div className="onboarding-title pb-2">{t('components.onboarding.consent.welcome')}</div>

                <div className="onboarding-subtitle">{t('components.onboarding.consent.sell-and-buy')}</div>

                {isBrandsImagVisible && (
                  <>
                    <div className="onboarding-seen pt-6 pb-3">{t('components.onboarding.consent.as-seen-on')}</div>

                    <div className="onboarding-brands pb-6">{brandsImg}</div>
                  </>
                )}
              </>
            )}

            {/* Re-confirming consent. */}
            {user?.requiresTermsAcceptance && (
              <div className="re-consent-container">
                <div className="onboarding-title re-consent pb-2">{t('components.onboarding.re-consent.title')}</div>
                <div className="onboarding-subtitle re-consent pb-5">{t('components.onboarding.re-consent.subtitle')}</div>
              </div>
            )}

            <div className="onboarding-consent">
              {/* Offers. */}
              {user.consentMarketingCommunications == null && !user.marketingEmailNotificationsAt && (
                <FormControlLabel
                  control={<Checkbox checked={acceptOffers} onChange={(e) => setAcceptOffers(e.target.checked)} color="primary" />}
                  label={t('components.onboarding.consent.offers')}
                  className={classNames('consent-checkbox no-highlight', { selected: acceptOffers })}
                />
              )}

              {/* Terms. */}
              <FormControlLabel
                control={
                  <Checkbox checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} color="primary" className="checkbox" />
                }
                label={
                  <span
                    dangerouslySetInnerHTML={{
                      __html: t('components.onboarding.consent.terms', {
                        termsUrl: YAGA_EXTERNALS.TERMS().href,
                        privacyUrl: YAGA_EXTERNALS.PRIVACY_POLICY().href,
                      }),
                    }}
                  />
                }
                className={classNames('consent-checkbox no-highlight', { selected: acceptTerms })}
              />
            </div>
          </div>
        );

      case OnboardingStep.USER_SETTINGS:
        return (
          <div className="onboarding onboarding-user-settings">
            <div className="onboarding-title pb-2">
              {t(isMissingName ? 'components.onboarding.user-settings.update' : 'components.onboarding.user-settings.title')}
            </div>

            <Grid container gap={3} direction="column">
              {!isMissingName && (
                <>
                  {availableCountries.length > 1 && (
                    <Grid item>
                      <Typography variant="subtitle1" className="pb-1">
                        {t('components.onboarding.country-label')}
                      </Typography>
                      <Dropdown
                        selectedItem={selectedCountry}
                        items={availableCountries.map((country, index) => ({
                          id: index,
                          title: t(`countries.${country.code}`),
                          value: country.code,
                        }))}
                        onSelect={(item) => setSelectedCountry(item)}
                        formControlProps={{
                          variant: 'outlined',
                          size: 'small',
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item>
                    <Typography variant="subtitle1" className="pb-1">
                      {t('components.onboarding.shop-name')}
                    </Typography>
                    <TextField
                      fullWidth={true}
                      color="primary"
                      variant="outlined"
                      value={shopName}
                      onChange={(e) => onChangeShopName(e.target.value)}
                      autoComplete="off"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      size="small"
                    />
                  </Grid>

                  <Grid item>
                    <Typography variant="subtitle1" color={palette.colorGray3} sx={{ lineHeight: 2 }}>
                      {t('components.onboarding.generated-slug')}
                      {shopName ? ':' : ''}
                    </Typography>
                    {shopName && shopSlug && (
                      <Typography
                        variant="subtitle1"
                        color={palette.colorGray3}
                        sx={{ fontWeight: 500, lineHeight: 1, whiteSpace: 'wrap', wordBreak: 'break-word' }}
                      >
                        <Grid container alignItems="center">
                          <Grid item>{window.location.origin}/</Grid>
                          {!isShopSlugLoading && <Grid item>{shopSlug}</Grid>}
                          <Grid item>
                            <CircularProgress disableShrink size={16} sx={{ visibility: !isShopSlugLoading ? 'hidden' : 'visible' }} />
                          </Grid>
                        </Grid>
                      </Typography>
                    )}
                  </Grid>
                </>
              )}

              <Grid item>
                <Typography variant="subtitle1" className="pb-1">
                  {t('common.first-name')}
                </Typography>
                <TextField
                  fullWidth={true}
                  color="primary"
                  variant="outlined"
                  value={firstName}
                  onChange={(e) => setFirstName(escapeEmoji(e.target.value))}
                  autoComplete="given-name"
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />
              </Grid>

              <Grid item>
                <Typography variant="subtitle1" className="pb-1">
                  {t('common.last-name')}
                </Typography>
                <TextField
                  fullWidth={true}
                  color="primary"
                  variant="outlined"
                  value={lastName}
                  onChange={(e) => setLastName(escapeEmoji(e.target.value))}
                  autoComplete="family-name"
                  inputProps={{ maxLength: 50 }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                />
              </Grid>
            </Grid>
          </div>
        );

      default:
        return null;
    }
  };

  const isMissingName = user?.isLoggedIn && user?.onboardingWebCompleted && (!user?.firstName || !user?.lastName);

  const getModalActions = () => {
    switch (currentStep) {
      case OnboardingStep.CONSENT:
        return [
          <Button
            variant="contained"
            color="primary"
            size="large"
            className="mt-1"
            key="onboarding-consent-action"
            fullWidth
            disabled={!acceptTerms}
            onClick={handleConsentCTA}
          >
            {t(!user?.requiresTermsAcceptance ? 'components.onboarding.consent.cta-signup' : 'components.onboarding.consent.cta-save')}
          </Button>,
        ];

      case OnboardingStep.USER_SETTINGS:
        return [
          <Button
            variant="contained"
            color="primary"
            size="large"
            className="mt-1"
            key="onboarding-user-settings-action"
            disabled={(!selectedCountry && !isMissingName) || !firstName.trim() || !lastName.trim()}
            fullWidth
            onClick={handleUserSettingsCTA}
          >
            {t('components.onboarding.user-settings.cta-continue')}
          </Button>,
        ];

      default:
        return [];
    }
  };

  useEffect(() => {
    if (user?.isLoggedIn) {
      if (isMissingName) {
        // Existing user missing first/last name — skip consent, go directly to user settings.
        setCurrentStep(OnboardingStep.USER_SETTINGS);
        setOnboardingVisible(true);
      } else {
        setOnboardingVisible(!user?.onboardingWebCompleted);
      }

      // Track start.
      if (!user?.onboardingWebCompleted && !trackingInited) {
        setTrackingInited(true);
        GTM.logEvent(GTMEvents.ONBOARDING_START, {
          userId: user?.id,
          reConsent: user?.requiresTermsAcceptance,
        });
      }
    }
  }, [user]);

  useEffect(() => {
    if (isOnboardingVisible) {
      // Pre-fetch countries for the user settings step
      fetchCountries();
    }
  }, [isOnboardingVisible]);

  useEffect(() => {
    if (availableCountries.length > 0) {
      const domainCountry =
        availableCountries.find((country) => hostnameEndsWithDomain(window.location.hostname, country.domain)) ??
        availableCountries.find((country) => country.code === config.get('country'));

      if (domainCountry) {
        // Pre-select country based on domain, if not detectable leave it blank.
        setSelectedCountry(createDropdownItem(t(`countries.${domainCountry.code}`), domainCountry.code));
      }
    }
  }, [availableCountries.length]);

  // No need to include modal in DOM at all if user is not logged in.
  if (!user?.isLoggedIn) {
    return null;
  }

  return (
    <Modal
      open={isOnboardingVisible}
      onClose={() => setOnboardingVisible(false)}
      disableBackdropDismiss
      disableEscapeKeyDown
      closeIconVisible={false}
      className="onboarding-modal"
      actions={getModalActions()}
    >
      {getModalContent()}
    </Modal>
  );
};
